/**
 * Sprint 38, story-204 (D5). Formats the daily report's next scheduled send as a localized day
 * + time in the ORGANIZATION timezone (never the device zone), e.g. "today at 8:00 PM CDT".
 * Pure: `now` is injected. Returns null when the timestamp is missing/invalid or the timezone is
 * not a valid IANA zone, so the caller falls back to copy without a time.
 */
export interface NextSendStrings {
  nextSendToday: string; // "today at {time}"
  nextSendTomorrow: string; // "tomorrow at {time}"
  nextSendWeekday: string; // "{day} at {time}"
  nextSendDate: string; // "{date} at {time}"
}

export type NextSendLocale = 'en' | 'es';

const INTL_LOCALE: Record<NextSendLocale, string> = { en: 'en-US', es: 'es-ES' };
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** YYYY-MM-DD of `date` in `timeZone`. */
const localDateKey = (date: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(date)
    .reduce<Record<string, string>>((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

const dayNumber = (key: string): number => {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / MS_PER_DAY;
};

const isValidTimeZone = (timeZone: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
};

export function formatNextSend(
  nextScheduledSendAt: string | Date | null | undefined,
  organizationTimezone: string | null | undefined,
  now: Date,
  locale: NextSendLocale,
  t: NextSendStrings,
): string | null {
  if (!nextScheduledSendAt || !organizationTimezone || !isValidTimeZone(organizationTimezone)) return null;
  const target = new Date(nextScheduledSendAt);
  if (Number.isNaN(target.getTime())) return null;

  const intlLocale = INTL_LOCALE[locale] ?? INTL_LOCALE.en;
  // Time: 12-hour in en ("8:00 PM"), 24-hour in es ("20:00"). The abbreviation always comes from
  // the en-US formatter ("CDT"); es-ES would print "GMT-5" for the same zone.
  const time = new Intl.DateTimeFormat(intlLocale, {
    timeZone: organizationTimezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: locale !== 'es',
  }).format(target);
  const abbreviation =
    new Intl.DateTimeFormat('en-US', { timeZone: organizationTimezone, timeZoneName: 'short' })
      .formatToParts(target)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';
  const timeLabel = abbreviation ? `${time} ${abbreviation}` : time;

  const daysAhead = dayNumber(localDateKey(target, organizationTimezone)) - dayNumber(localDateKey(now, organizationTimezone));
  if (daysAhead <= 0) return t.nextSendToday.replace('{time}', timeLabel);
  if (daysAhead === 1) return t.nextSendTomorrow.replace('{time}', timeLabel);
  if (daysAhead < 7) {
    const day = new Intl.DateTimeFormat(intlLocale, { timeZone: organizationTimezone, weekday: 'long' }).format(target);
    return t.nextSendWeekday.replace('{day}', day).replace('{time}', timeLabel);
  }
  const date = new Intl.DateTimeFormat(intlLocale, {
    timeZone: organizationTimezone,
    month: 'short',
    day: 'numeric',
  }).format(target);
  return t.nextSendDate.replace('{date}', date).replace('{time}', timeLabel);
}
